import { safeJson } from "src/app/helpers/Index";
import { faCircleCheck, faMoneyBill, faQrcode, faTicket } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import React from "react";
import { Allocations } from "src/app/components/Allocations";
import { SaleWindows } from "src/app/components/SaleWindows";
import { AllocationParent, SaleWindowParent } from "src/app/services/Index";
import { TicketingService } from "src/app/services/TicketingService";
import "./FixtureTicketingDetail.css";

type Props = {
	fixture: any;
	club: any;
	config: any;
	onConfigChange: (patch: any) => void;
	onSetPrice: (ticketTypeId: number, pounds: number | null) => void;
	onEdit?: () => void;
	/** The matchday editor, when this club has the scoreboard. */
	matchdaySlot?: React.ReactNode;
	/** Ticketing off means capacity, prices and sale windows don't apply. */
	ticketingEnabled?: boolean;
	onError?: (message: string) => void;
	onInfo?: (message: string) => void;
};

type State = {
	tab: "sales" | "windows" | "allocations" | "prices" | "matchday";
	stats: any;
};

const money = (pence: any) =>
	`£${((Number(pence) || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const toPounds = (pence: any) => (pence == null ? null : Number(pence) / 100);

/**
 * A fixture's ticketing, laid out the way a club reads it: what it's worth,
 * what's gone, how full it is — then the controls.
 *
 * The numbers come first because that's the question someone opens this page
 * with. Configuration is what you do occasionally; checking sales is what you
 * do every day.
 */
export class FixtureTicketingDetail extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { tab: "sales", stats: null };
	}

	componentDidMount() {
		this.loadStats();
	}

	componentDidUpdate(prev: Props) {
		if (prev.fixture?.id !== this.props.fixture?.id) {
			this.setState({ stats: null, tab: "sales" });
			this.loadStats();
		}
	}

	async loadStats() {
		const { fixture } = this.props;
		if (!fixture?.id) return;
		try {
			const stats = await safeJson(TicketingService.getFixtureStats(fixture.id));
			this.setState({ stats });
		} catch { /* the cards show dashes */ }
	}

	/** The jobs a club has to do before a fixture is genuinely on sale. */
	todo() {
		const { config, fixture } = this.props;
		const { stats } = this.state;
		return [
			{ done: !!fixture?.competitionId, label: "Set the competition" },
			{ done: !!config?.capacity, label: "Assign a capacity" },
			{ done: (config?.prices || []).some((p: any) => p.pricePence > 0), label: "Price the tickets" },
			{ done: !!config?.enabled, label: "Put it on sale" },
			{ done: (stats?.ticketsIssued || 0) > 0, label: "Review ticket sales" }
		];
	}

	renderHeader() {
		const { fixture, club, config, onConfigChange, onEdit, ticketingEnabled = true } = this.props;
		const kickOff = new Date(fixture.fixtureDateTime);

		return (
			<div className="ft-top">
				<div className="ft-card ft-matchcard">
					{onEdit && <button className="ft-edit" onClick={onEdit} type="button">Edit fixture</button>}
					<div className="ft-teams">
						<div className="ft-team">
							{club?.badgeUrl && <img alt="" src={club.badgeUrl} />}
							<span>{club?.name || "Home"}</span>
						</div>
						<span className="ft-vs">vs</span>
						<div className="ft-team">
							{(fixture.teamBadgeUrl || fixture.team?.badgeUrl) && <img alt="" src={fixture.teamBadgeUrl || fixture.team.badgeUrl} />}
							<span>{fixture.teamName || fixture.team?.teamName || "Visitors"}</span>
						</div>
					</div>
					<div className="ft-strip">
						{kickOff.toLocaleDateString("en-GB", { day: "numeric", month: "short", weekday: "short", year: "2-digit" })}
						{" | "}{kickOff.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
						{(fixture.competitionName || fixture.competition?.name) ? ` | ${fixture.competitionName || fixture.competition.name}` : ""}
					</div>
					{ticketingEnabled && <div className="ft-onsale">
						<InputSwitch checked={!!config?.enabled} onChange={(e) => onConfigChange({ enabled: e.value })} />
						<span className={config?.enabled ? "ft-live" : "ft-off"}>
							{config?.enabled ? "On sale" : "Not on sale"}
						</span>
					</div>}
				</div>

				<div className="ft-card ft-todo">
					<h4>To do</h4>
					{this.todo().map((item) => (
						<div className={`ft-todo-row${item.done ? " done" : ""}`} key={item.label}>
							<FontAwesomeIcon icon={faCircleCheck} />
							<span>{item.label}</span>
						</div>
					))}
				</div>
			</div>
		);
	}

	renderStats() {
		const s = this.state.stats;
		const cards = [
			{
				icon: faMoneyBill, label: "Revenue", note: "Tickets sold through Pitchbase",
				value: s ? money(s.revenuePence) : "—"
			},
			{
				icon: faTicket, label: "Tickets issued",
				note: s?.seasonTicketHolders ? `Plus ${s.seasonTicketHolders} season tickets` : "Single-fixture tickets",
				value: s ? String(s.ticketsIssued) : "—"
			},
			{
				icon: faTicket, label: "Comped", note: "Given away, not sold",
				value: s ? String(s.compedTickets) : "—"
			},
			{
				icon: faQrcode, label: "Attendance",
				note: "Tickets, hospitality and season tickets",
				value: s ? `${s.attendancePercent}%` : "—",
				sub: s ? `${s.scanned} / ${s.expected}` : ""
			}
		];

		return (
			<div className="ft-stats">
				{cards.map((c) => (
					<div className="ft-card ft-stat" key={c.label}>
						<h5>{c.label}</h5>
						<small>{c.note}</small>
						<div className="ft-stat-value">
							<span className="ft-stat-icon"><FontAwesomeIcon icon={c.icon} /></span>
							<strong>{c.value}</strong>
						</div>
						{c.sub && <span className="ft-stat-sub">{c.sub}</span>}
					</div>
				))}
			</div>
		);
	}

	renderSales() {
		const { config, onConfigChange } = this.props;
		const s = this.state.stats;

		return (
			<>
				<div className="ft-capacity">
					<div>
						<label>Standard capacity</label>
						<InputNumber onValueChange={(e) => onConfigChange({ capacity: e.value })} placeholder="Uncapped" value={config?.capacity} />
						{s?.allocatedTickets > 0 &&
							<small>{s.allocatedTickets} held in allocations</small>}
					</div>
					<div>
						<label>On sale from</label>
						<Calendar dateFormat="dd/mm/yy" onChange={(e) => onConfigChange({ onSaleFrom: e.value })} showTime value={config?.onSaleFrom ? new Date(config.onSaleFrom) : undefined} />
					</div>
					<div>
						<label>Off sale at</label>
						<Calendar dateFormat="dd/mm/yy" onChange={(e) => onConfigChange({ offSaleAt: e.value })} showTime value={config?.offSaleAt ? new Date(config.offSaleAt) : undefined} />
					</div>
				</div>

				{!config?.enabled &&
					<p className="ft-warn">
						This fixture isn&rsquo;t on sale, so none of the above is doing anything yet.
					</p>}
			</>
		);
	}

	renderPrices() {
		const { config, onSetPrice } = this.props;
		const prices = config?.prices || [];

		if (!prices.length) {
			return <p className="ft-none">Add ticket types first, under Ticket Types.</p>;
		}

		return (
			<>
				<p className="ft-none" style={{ marginTop: 0 }}>
					Overrides for this fixture only. Anything left on its default follows the price category.
				</p>
				{prices.map((price: any) => (
					<div className="ft-price-row" key={`p-${price.ticketTypeId}`}>
						<span>{price.name}</span>
						<InputNumber currency="GBP" locale="en-GB" mode="currency"
							onValueChange={(e) => onSetPrice(price.ticketTypeId, e.value ?? null)}
							value={toPounds(price.pricePence)} />
						{price.overridden
							? <Button className="p-button-text" label="Use default" onClick={() => onSetPrice(price.ticketTypeId, null)} style={{ padding: "3px 8px" }} />
							: <span className="ft-default">default</span>}
					</div>
				))}
			</>
		);
	}

	render() {
		const { fixture, onError, onInfo, matchdaySlot, ticketingEnabled = true } = this.props;
		if (!fixture) return null;

		// A club sees the tabs for what it actually has. Scoreboard-only clubs
		// get the matchday editor and nothing about selling; ticketing-only
		// clubs get the reverse. Showing both to everyone would mean half this
		// screen is dead weight for most clubs.
		const tabs: { key: State["tab"]; label: string }[] = [];
		if (ticketingEnabled) {
			tabs.push({ key: "sales", label: "Ticket sales" });
			tabs.push({ key: "prices", label: "Prices" });
			tabs.push({ key: "windows", label: "Sale windows" });
			tabs.push({ key: "allocations", label: "Allocations" });
		}
		if (matchdaySlot) tabs.push({ key: "matchday", label: "Matchday" });

		// Whatever is first is what opens, so the default lands on a tab that
		// exists rather than a blank panel.
		const tab = tabs.some((t) => t.key === this.state.tab) ? this.state.tab : tabs[0]?.key;

		return (
			<div className="ft-wrap">
				{this.renderHeader()}
				{this.renderStats()}

				<div className="ft-tabs">
					{tabs.map((t) => (
						<button className={`ft-tab${tab === t.key ? " active" : ""}`} key={t.key} onClick={() => this.setState({ tab: t.key })} type="button">
							{t.label}
						</button>
					))}
				</div>

				<div className="ft-panel">
					{tab === "sales" && this.renderSales()}
					{tab === "prices" && this.renderPrices()}
					{tab === "windows" &&
						<SaleWindows onError={onError} parentId={fixture.id} parentType={SaleWindowParent.Fixture} showPriority />}
					{tab === "allocations" &&
						<Allocations onError={onError} onInfo={onInfo} parentId={fixture.id} parentType={AllocationParent.Fixture} />}
					{tab === "matchday" && matchdaySlot}
				</div>
			</div>
		);
	}
}
